import { useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Checkbox,
  Dropdown,
  Icon,
  Image,
  Menu,
  SemanticCOLORS,
} from 'semantic-ui-react';

import Logo from '../../assets/xpaslogo.png';
import { ROLES } from '../../libs/Constants';
import RoleCheck from '../../libs/RoleCheck';
import HelpModal from '../MainHeader/components/HelpModal';
import SignInMenu from '../MainHeader/components/SignInMenu';
import TeamPendingStats from '../MainHeader/components/TeamPendingStats';
import UserMenu from '../MainHeader/components/UserMenu';
import {
  ADMIN_LABEL,
  AUTO_ASSIGN_LABEL,
  CMS_IPO_LIST_LABEL,
  DASHBOARD_LABEL,
  QUEUE_LABEL,
  REPORTING_LABEL,
  SEARCH_LABEL,
  TOOLS_LABEL,
  VERSION_TEXT,
} from '../MainHeader/MainHeader.constants';
import { TeamStats, UserRole } from '../MainHeader/MainHeader.types';

interface CortexPaHeaderProps {
  userToken: string | null;
  teamStats: TeamStats[];
  totalPending: number;
  colors: SemanticCOLORS[];
  userRoles: UserRole[];
  toggleWorking: () => void;
  iamworking: boolean;
  username: string;
  handleProfile: () => void;
  handleLogout: () => void;
  userGuideUrl: string;
  appVersion: string;
}

// CORTEX.PA Header component
// This component renders the header for the Cortex Physician Advisor application.
// It is used for clients on Merlot system and will be removed soon, once all
// clients are migrated to the new Dragonfly system.

const CortexPaHeader = ({
  userToken,
  teamStats,
  totalPending,
  colors,
  userRoles,
  toggleWorking,
  iamworking,
  username,
  handleProfile,
  handleLogout,
  userGuideUrl,
  appVersion,
}: CortexPaHeaderProps) => {
  const handleQueueClick = useCallback(() => {
    sessionStorage.removeItem('selectedPopulation');
  }, []);

  const hasReportingAccess = useMemo(() => {
    return RoleCheck(userRoles, [
      ROLES.SYSTEM_ADMINISTRATOR,
      ROLES.ADMINISTRATOR,
      ROLES.TEAM_LEAD,
      ROLES.SUPPORT,
    ]);
  }, [userRoles]);

  const hasAdminAccess = useMemo(() => {
    return RoleCheck(userRoles, [
      ROLES.SYSTEM_ADMINISTRATOR,
      ROLES.ADMINISTRATOR,
    ]);
  }, [userRoles]);

  const hasPhysicianAdvisorAccess = useMemo(() => {
    return RoleCheck(userRoles, [ROLES.PHYSICIAN_ADVISOR]);
  }, [userRoles]);

  return (
    <div className='TopMenu'>
      <Menu
        inverted
        attached
        className='app-header borderless'
        style={{ backgroundColor: '#262662' }}
      >
        <Menu.Item>
          <NavLink to='/'>
            <Image style={{ display: 'inline', width: '125px' }} src={Logo} />
          </NavLink>
          {VERSION_TEXT} {appVersion}
        </Menu.Item>

        {userToken ? (
          <Menu.Menu position='right'>
            <TeamPendingStats
              teamStats={teamStats}
              totalPending={totalPending}
              colors={colors}
            />
            <Menu.Item>
              <NavLink to='/dashboard'>
                <Icon name='dashboard' />
                {DASHBOARD_LABEL}
              </NavLink>
            </Menu.Item>
            <Menu.Item>
              <NavLink to='/queue' onClick={handleQueueClick}>
                <Icon name='tasks' />
                {QUEUE_LABEL}
              </NavLink>
            </Menu.Item>
            {hasReportingAccess && (
              <Menu.Item>
                <NavLink to='/reporting'>
                  <Icon name='area chart' />
                  {REPORTING_LABEL}
                </NavLink>
              </Menu.Item>
            )}
            {hasAdminAccess && (
              <Menu.Item>
                <NavLink to='/admin'>
                  <Icon name='users' />
                  {ADMIN_LABEL}
                </NavLink>
              </Menu.Item>
            )}
            <Menu.Item>
              <NavLink to='/search'>
                <Icon name='search' />
                {SEARCH_LABEL}
              </NavLink>
            </Menu.Item>
            <Menu.Item>
              <Dropdown
                className='navigation-menu-item'
                icon={<Icon name='wrench' />}
                trigger={TOOLS_LABEL}
              >
                <Dropdown.Menu>
                  <Dropdown.Item
                    data-cms-link='data-cms-link'
                    as={NavLink}
                    to={'/cms'}
                  >
                    {CMS_IPO_LIST_LABEL}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Menu.Item>
            {hasPhysicianAdvisorAccess && (
              <Menu.Item>
                <Checkbox
                  className='ui white'
                  data-testid='auto-assign-checkbox'
                  label={AUTO_ASSIGN_LABEL}
                  onClick={toggleWorking}
                  checked={iamworking}
                />
              </Menu.Item>
            )}
            <Menu.Item>
              <UserMenu
                username={username}
                handleProfileClick={handleProfile}
                handleLogoutClick={handleLogout}
              />
            </Menu.Item>

            <Menu.Item>
              <HelpModal userGuideUrl={userGuideUrl} />
            </Menu.Item>
          </Menu.Menu>
        ) : (
          <SignInMenu />
        )}
      </Menu>
    </div>
  );
};

export default CortexPaHeader;
